/**
 * NotificationContext — Hybrid notification system.
 *
 * Persistent notifications: fetched from backend (invites, role changes, etc.)
 * Ephemeral notifications: client-side only (generation progress, local events)
 *
 * Polls backend every 30s for new notifications.
 */
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  getMyNotifications,
  getUnreadCount,
  markNotificationRead as apiMarkRead,
  markAllNotificationsRead as apiMarkAllRead,
  deleteNotification as apiDelete,
} from '@/services/teamService';

const NotificationContext = createContext(null);

const POLL_INTERVAL = 30000; // 30 seconds

// Ephemeral notification types (client-side only)
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning',
  GENERATION: 'generation',
};

export const NOTIFICATION_CATEGORIES = {
  CHARACTERS: 'characters',
  LOCATIONS: 'locations',
  SCENES: 'scenes',
  STORYBOARD: 'storyboard',
  SCRIPT: 'script',
  GENERAL: 'general',
};

export function NotificationProvider({ children }) {
  // Persistent notifications (from backend)
  const [persistent, setPersistent] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Ephemeral notifications (client-side only, for generation progress etc.)
  const [ephemeral, setEphemeral] = useState([]);

  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  // Fetch persistent notifications from backend
  const fetchNotifications = useCallback(async () => {
    try {
      const [notifs, count] = await Promise.all([
        getMyNotifications(50, 0),
        getUnreadCount(),
      ]);
      if (mountedRef.current) {
        setPersistent(notifs);
        setUnreadCount(count);
      }
    } catch (err) {
      // Silently fail — user may not be authenticated yet
    }
  }, []);

  // Start polling on mount
  useEffect(() => {
    mountedRef.current = true;
    fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchNotifications]);

  // Combined notifications list (persistent first, then ephemeral)
  const notifications = [...persistent, ...ephemeral];

  // ── Persistent notification actions ──

  const markAsRead = useCallback(async (notificationId) => {
    // Check if it's ephemeral
    const isEphemeral = ephemeral.some((n) => n.id === notificationId);
    if (isEphemeral) {
      setEphemeral((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));
      return;
    }
    try {
      await apiMarkRead(notificationId);
      setPersistent((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, [ephemeral]);

  const markAllAsRead = useCallback(async () => {
    try {
      await apiMarkAllRead();
      setPersistent((prev) => prev.map((n) => ({ ...n, read: true })));
      setEphemeral((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, []);

  const clearNotification = useCallback(async (notificationId) => {
    const isEphemeral = ephemeral.some((n) => n.id === notificationId);
    if (isEphemeral) {
      setEphemeral((prev) => prev.filter((n) => n.id !== notificationId));
      return;
    }
    try {
      await apiDelete(notificationId);
      setPersistent((prev) => {
        const notif = prev.find((n) => n.id === notificationId);
        if (notif && !notif.read) setUnreadCount((c) => Math.max(0, c - 1));
        return prev.filter((n) => n.id !== notificationId);
      });
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  }, [ephemeral]);

  // ── Ephemeral notification actions (kept for backward compat with ScenesPage etc.) ──

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: `ephemeral-${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      created_at: new Date().toISOString(),
      read: false,
      _ephemeral: true,
      ...notification,
    };
    setEphemeral((prev) => [newNotification, ...prev].slice(0, 50));
    return newNotification.id;
  }, []);

  const markCategoryAsRead = useCallback((category) => {
    setEphemeral((prev) => prev.map((n) => (n.category === category ? { ...n, read: true } : n)));
  }, []);

  const clearAll = useCallback(() => {
    setEphemeral([]);
  }, []);

  const getUnreadByCategory = useCallback(
    (category) => ephemeral.filter((n) => n.category === category && !n.read).length,
    [ephemeral],
  );

  // Total unread = backend unread + ephemeral unread
  const totalUnread = unreadCount + ephemeral.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        persistent,
        ephemeral,
        unreadCount: totalUnread,
        addNotification,
        markAsRead,
        markCategoryAsRead,
        markAllAsRead,
        clearNotification,
        clearAll,
        getUnreadByCategory,
        fetchNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
